import type { Subscription, SubscriptionLine, Holiday, HolidayRequest, LineAmendment, LineFreeze } from '@/types'
import { toLocalDateString } from '@/utils/deliveryDays'

/**
 * Turning a subscription (the standing order) into actual dated deliveries.
 *
 * Shared by My Deliveries, Delivery Management and the weekly order. Kept here
 * rather than inside a component so they can never drift on what "what am I
 * getting, and when" means.
 */

export const toStartOfDay = (value: string | Date): Date => {
  const date = value instanceof Date ? new Date(value) : new Date(value)
  date.setHours(0, 0, 0, 0)
  return date
}

/**
 * Is the line paused on this date by a Line Freeze (DF-350)? A freeze leaves
 * the line active, so without this a paused product still showed as arriving.
 */
export const isLineFrozenOn = (lineId: string, date: Date, freezes: LineFreeze[]): boolean => {
  if (freezes.length === 0) return false
  const day = toLocalDateString(date)
  return freezes.some(f => f.subscription_line_id === lineId
    && f.effective_from <= day && day <= f.effective_to)
}

/** Convert API holiday requests into the internal calendar shape. */
export const convertToCalendarHolidays = (requests: HolidayRequest[]): Holiday[] => {
  return requests
    .filter(req => req.statecode === 0) // Only active holidays (0 = active, 1 = inactive)
    .filter(req => req.start_date && req.end_date)
    .map(req => ({
      id: req.id,
      // Normalize to date-only to avoid timezone offsets excluding the first day.
      start: toStartOfDay(req.start_date),
      // `end_date` is the restart date, so it is excluded from the paused period.
      end: toStartOfDay(req.end_date),
      label: 'Holiday',
    }))
}

export interface DeliveryDate {
  id: number
  date: Date
  isHoliday: boolean
  isNext: boolean
  isEditable: boolean
  /** Already been and gone. Kept for the current week so it can show faded. */
  isPast: boolean
  /**
   * A new customer's first delivery, still being set up, so it can't be edited
   * or paused yet (FEDEV-544). Also forces isEditable false.
   */
  firstDeliveryLocked: boolean
  items: SubscriptionLine[]
  allActiveLines: SubscriptionLine[]
}

/** A delivery is editable when its date is after today. Today is always locked. */
export const isEditableDeliveryDate = (date: Date): boolean => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return toStartOfDay(date) > today
}

/** Does a date fall in a holiday? End date exclusive — used for display. */
export const isDateInHoliday = (date: Date, holidays: Holiday[]): boolean => {
  const dateOnly = toStartOfDay(date)
  return holidays.some(h => dateOnly >= h.start && dateOnly < h.end)
}

/**
 * Are changes blocked by an active holiday? The end date is inclusive here:
 * changes remain blocked on the end date itself.
 */
export const isBlockedByActiveHoliday = (date: Date, holidays: Holiday[]): boolean => {
  const dateOnly = toStartOfDay(date)
  return holidays.some(h => dateOnly >= h.start && dateOnly <= h.end)
}

/**
 * A line that can never deliver: cancelled before it started, which the backend
 * records by ending it the day BEFORE its start date, so the range runs
 * backwards. The row stays in CRM, so anything listing a customer's order has
 * to recognise and skip it.
 *
 * The test is strictly end < start. end_date is the INCLUSIVE last delivery, so
 * a line whose end equals its start still delivers once: that is a valid
 * single delivery (a product added "for 1 week" on a weekly day, DF-312), not
 * an empty range. Using <= here hid those single deliveries from the order.
 */
export const neverDelivers = (line: SubscriptionLine): boolean => {
  if (!line.start_date || !line.end_date) return false
  return toStartOfDay(line.end_date) < toStartOfDay(line.start_date)
}

/**
 * The next date this line actually delivers, or null if it never will again.
 *
 * A line's own weekday is what decides this, not its end date alone: a
 * Thursday line ending on Wednesday 29th has a future end date but no Thursday
 * left in it, so it is finished. Anything judging "is this still part of my
 * order" has to ask this rather than compare end_date to today, or it will
 * keep showing (and charging for) a line that can never deliver.
 */
export const nextDeliveryDateFor = (line: SubscriptionLine): Date | null => {
  if (line.statecode === 1) return null
  if (line.delivery_day === null || line.delivery_day === undefined) return null

  let from = toStartOfDay(new Date())
  if (line.start_date) {
    const start = toStartOfDay(line.start_date)
    if (start > from) from = start
  }

  // Step forward to the line's own weekday.
  const next = new Date(from)
  next.setDate(from.getDate() + ((line.delivery_day - from.getDay() + 7) % 7))

  if (line.end_date && next > toStartOfDay(line.end_date)) return null

  return next
}

/**
 * The final date this line delivers, or null if it runs indefinitely.
 *
 * The end date is a boundary, not a delivery: a Thursday line ending on
 * Saturday the 1st last delivers on Thursday the 30th. Stepping back to the
 * line's own weekday is what turns the boundary into the date a customer
 * actually cares about.
 */
export const lastDeliveryDateFor = (line: SubscriptionLine): Date | null => {
  if (!line.end_date) return null
  if (line.delivery_day === null || line.delivery_day === undefined) return null

  const end = toStartOfDay(line.end_date)
  const last = new Date(end)
  last.setDate(end.getDate() - ((end.getDay() - line.delivery_day + 7) % 7))

  return last
}

/** Is a subscription line live on a given delivery date? */
/** The line's start date, but only when it is still to come. */
export const startDateIfFuture = (line: SubscriptionLine): Date | null => {
  if (!line.start_date) return null

  const start = toStartOfDay(line.start_date)
  return start > toStartOfDay(new Date()) ? start : null
}

/**
 * An open-ended change effective from the line's first delivery or earlier.
 *
 * On a line that hasn't started, that isn't a change to anything: it is the
 * quantity the product will begin at. Both the weekly panel and the change
 * dialog read it, so a card and the editor it opens can't disagree about what
 * the customer currently gets.
 */
export const changeFromTheStart = (
  line: SubscriptionLine,
  amendments: LineAmendment[]
): LineAmendment | null => {
  const starts = startDateIfFuture(line)
  if (!starts) return null

  return amendments.find(a => a.subscription_line_id === line.id
    && !a.effective_to
    && toStartOfDay(a.effective_from) <= starts) ?? null
}

/**
 * The standing quantity on a line: what the customer gets week in, week out,
 * ignoring bounded changes. Those are deviations, surfaced separately.
 */
export const standingQuantityFor = (
  line: SubscriptionLine,
  amendments: LineAmendment[]
): number => changeFromTheStart(line, amendments)?.new_quantity ?? line.quantity

export const isLineActiveOnDate = (line: SubscriptionLine, deliveryDate: Date): boolean => {
  if (line.statecode === 1) return false

  const deliveryDateOnly = toStartOfDay(deliveryDate)

  // Cancelled before it started: end on or before start, so it never delivers.
  if (neverDelivers(line)) return false

  // Must have started by the delivery date.
  if (line.start_date && toStartOfDay(line.start_date) > deliveryDateOnly) {
    return false
  }

  // end_date is the INCLUSIVE last delivery day (business confirmed: a product
  // ending on the 29th is still delivered on the 29th), so a line is only
  // inactive strictly after it. Removals honour this by ending the line the day
  // before the delivery being removed (SubscriptionLineController).
  if (line.end_date && toStartOfDay(line.end_date) < deliveryDateOnly) {
    return false
  }

  return true
}

/**
 * Overlay pending quantity amendments onto lines for one delivery date.
 *
 * An amendment applies from its effective_from up to and including its
 * effective_to. A bounded amendment must stop applying after that date,
 * otherwise a "3 instead of 2 for three weeks" change would project forward
 * forever and every later delivery would be priced wrong.
 */
export const getMergedSubscriptionLines = (
  currentLines: SubscriptionLine[],
  amendments: LineAmendment[],
  effectiveDate: Date
): SubscriptionLine[] => {
  const target = toStartOfDay(effectiveDate)

  return currentLines.map(line => {
    // A line can carry several amendments covering the same date, and CRM does
    // not stop them overlapping. The one that started most recently is the one
    // in force; taking the first match meant which quantity won came down to
    // the order the API happened to return them in.
    const amendment = amendments
      .filter(a => {
        if (a.subscription_line_id !== line.id) return false
        if (toStartOfDay(a.effective_from) > target) return false
        if (a.effective_to && toStartOfDay(a.effective_to) < target) return false
        return true
      })
      .sort((a, b) => toStartOfDay(b.effective_from).getTime()
        - toStartOfDay(a.effective_from).getTime())[0]

    if (amendment) {
      return {
        ...line,
        quantity: amendment.new_quantity,
        subtotal: amendment.new_quantity * line.unit_price,
        subtotal_inc_vat: amendment.new_quantity * (line.unit_price_inc_vat ?? line.unit_price),
        isPending: true,
        effectiveDate: amendment.effective_from,
      }
    }

    return line
  })
}

/** A calendar date as a whole day count, so week maths ignores clock changes. */
const dayNumber = (value: string | Date): number => {
  const [year, month, day] = (typeof value === 'string' ? value.slice(0, 10) : toLocalDateString(value))
    .split('-').map(Number)
  return Date.UTC(year, month - 1, day) / 86400000
}

/**
 * Does a date fall on a delivery week for a schedule that started on startDate
 * and repeats every `frequency` weeks? 1 (or null) = weekly, 2 = fortnightly,
 * and so on.
 *
 * A non-weekly product follows its subscription's existing delivery weeks,
 * counted from the subscription's start date (DF-380). The same rule decides
 * which weeks My Deliveries shows a product on and which start dates are
 * offered when one is added, so a product always appears on the date it was
 * added from.
 */
export const isDeliveryWeek = (
  date: string | Date,
  startDate: string | null | undefined,
  frequency: number | string | null | undefined
): boolean => {
  const every = Number(frequency) || 1
  if (every <= 1 || !startDate) return true

  const diffWeeks = Math.floor((dayNumber(date) - dayNumber(startDate)) / 7)

  return diffWeeks >= 0 && diffWeeks % every === 0
}

/** isDeliveryWeek for a subscription's own start date and frequency. */
export const isDeliveryWeekForSubscription = (date: Date, subscription: Subscription): boolean =>
  isDeliveryWeek(date, subscription.start_date, subscription.billing_frequency)

/** Project the standing order forward into dated deliveries. */
export const generateDeliveryDates = (
  subscriptions: Subscription[],
  holidays: Holiday[],
  amendments: LineAmendment[],
  scheduledDeliveryDays: number[] = [],
  weeksAhead: number = 8,
  maxDeliveries: number = 8,
  // Dates (YYYY-MM-DD) of new customers' first deliveries, locked from edits
  // while their order is set up (FEDEV-544).
  lockedDates: Set<string> = new Set(),
  // Temporary product pauses (DF-350): a frozen line skips those deliveries.
  freezes: LineFreeze[] = []
): DeliveryDate[] => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const activeSubscriptions = subscriptions.filter(sub => sub.statecode !== 1)

  const deliveryDaysSet = new Set<number>()
  activeSubscriptions.forEach(sub => {
    sub.lines.forEach(line => {
      // The delivery surcharge is not a delivery; it is shown under the weekly
      // total, never as its own day or item (DF-88). A one-off product is shown
      // in its own box, not among the day-by-day products (DF-230).
      if (line.is_surcharge || line.is_one_off) return
      if (line.delivery_day !== null && line.delivery_day !== undefined) {
        deliveryDaysSet.add(line.delivery_day)
      }
    })
  })

  // FEDEV-554: the tabs used to be built from the lines alone, so removing
  // every item from a day (or a day whose items never synced) made the whole
  // day disappear, even though the customer is still scheduled to receive on
  // it. Fold in the customer's authoritative delivery days so a configured day
  // always has a tab.
  //
  // emptyScheduledDays are the configured days that currently have no line at
  // all. Only those get an empty, add-to-me card; days that do have lines keep
  // their exact existing behaviour, including hiding fortnightly off-weeks.
  // Only when the customer already has an active subscription. A brand-new
  // customer whose first delivery is still being set up has no active sub yet
  // (pending subs are hidden), and must keep seeing the "first delivery being
  // set up" message rather than a grid of empty day cards (BUG-8).
  const emptyScheduledDays = activeSubscriptions.length > 0
    ? scheduledDeliveryDays.filter(day => !deliveryDaysSet.has(day))
    : []
  emptyScheduledDays.forEach(day => deliveryDaysSet.add(day))

  const deliveryDays = Array.from(deliveryDaysSet).sort((a, b) => a - b)
  if (deliveryDays.length === 0) return []

  const dates: DeliveryDate[] = []
  const endDate = new Date(today)
  endDate.setDate(today.getDate() + weeksAhead * 7)

  // Start from the Monday of the current week, so a delivery earlier this week
  // that has already been (e.g. this week's Tuesday) still appears, shown faded,
  // rather than the week looking empty.
  const current = new Date(today)
  current.setDate(today.getDate() - ((today.getDay() + 6) % 7))
  let id = 1

  while (current <= endDate) {
    const dayOfWeek = current.getDay()

    if (deliveryDays.includes(dayOfWeek)) {
      const currentDate = new Date(current)

      // Collect items from every subscription delivering on this date,
      // respecting each one's billing frequency.
      const items: SubscriptionLine[] = []
      const allActiveLines: SubscriptionLine[] = []

      for (const sub of activeSubscriptions) {
        if (!isDeliveryWeekForSubscription(currentDate, sub)) continue

        const mergedLines = getMergedSubscriptionLines(sub.lines.filter(l => !l.is_surcharge && !l.is_one_off), amendments, currentDate)

        const delivering = (line: SubscriptionLine) =>
          isLineActiveOnDate(line, currentDate) && !isLineFrozenOn(line.id, currentDate, freezes)

        allActiveLines.push(...mergedLines.filter(delivering))

        items.push(...mergedLines.filter(line =>
          line.delivery_day === dayOfWeek && delivering(line)
        ))
      }

      // An empty scheduled day still gets a card, so the customer can see it
      // and add to it. A day with items keeps the existing rule (only shown
      // when it actually delivers this week), so fortnightly off-weeks stay
      // hidden.
      if (items.length > 0 || emptyScheduledDays.includes(dayOfWeek)) {
        const locked = lockedDates.has(toLocalDateString(currentDate))
        dates.push({
          id: id++,
          date: currentDate,
          isHoliday: isDateInHoliday(current, holidays),
          // Set in a second pass so it can target the first editable delivery.
          isNext: false,
          // A locked first delivery is never editable (FEDEV-544).
          isEditable: isEditableDeliveryDate(currentDate) && !locked,
          isPast: currentDate < today,
          firstDeliveryLocked: locked,
          items,
          allActiveLines,
        })
      }
    }

    current.setDate(current.getDate() + 1)
  }

  const trimmed = dates.slice(0, maxDeliveries)
  const nextIndex = trimmed.findIndex(d => d.isEditable && !d.isHoliday)
  if (nextIndex !== -1) {
    trimmed[nextIndex].isNext = true
  }
  return trimmed
}
